import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Word,
  GameState,
  Coordinates,
  GameSetup,
  GameWithDate,
} from '../../../../types/types';
import { getTileKey, getGameStateFromSetup } from './PuzzleBuilderEngine';
import { formatDateToYYYYMMDD } from '../../../../helpers';
import { getAllValidPathsForGame } from './searchEngine';
import { usePostGame } from '../../../../helpers/useApi';

import './PuzzleBuilder.css';
import Typo from 'typo-js';
import { getSolvedPuzzle } from '../../../../helpers/gameSolver';

type ValidationResult = {
  success: boolean;
  validationFailureReason?: string | undefined;
};

export function PuzzleBuilder(props: Props) {
  const [words, setWords] = useState<Word[]>([]);
  const [input, setInput] = useState('');
  const [xInput, setXInput] = useState(0);
  const [yInput, setYInput] = useState(0);
  const [theme, setTheme] = useState('');
  const [date, setDate] = useState(formatDateToYYYYMMDD(new Date()));
  const [solutions, setSolutions] = useState<GameState[]>([]);
  const [overrideSpelling, setOverrideSpelling] = useState(false);

  const postGameMutation = usePostGame();

  const addWord = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed) {
      setWords([...words, { text: trimmed, offset: 0 }]);
      setInput('');
    }
  };

  const removeWord = (index: number) => {
    setWords(words.filter((_, i) => i !== index));
  };

  function createGameSetup(coords: Coordinates): GameSetup {
    return {
      words,
      pathString: '',
      startCoordinates: coords,
      theme,
    };
  }

  function validateAndUploadGame(overrideSpellCheck?: boolean) {
    if (!props.gameState) return;

    const gameSetup: GameSetup = {
      words,
      pathString: props.gameState.pathLetters.join(''),
      startCoordinates: props.gameState.path[0],
      theme,
    };

    const gameWithDate: Omit<GameWithDate, '_id'> = {
      date,
      game: gameSetup,
    };

    const isValid = isGameValid(gameWithDate, overrideSpellCheck);
    if (!isValid.success) {
      alert(`Game is not valid: ${isValid.validationFailureReason}`);
      return;
    }

    postGameMutation.mutate({ game: gameWithDate });
  }

  function SearchGames() {
    if (!props.gameState) return;

    const gameSetup: GameSetup = {
      words,
      pathString: props.gameState.pathLetters.join(''),
      startCoordinates: props.gameState.path[0],
      theme,
    };

    const validGames = getAllValidPathsForGame(gameSetup);
    setSolutions(validGames);
  }

  function getRandomSolution() {
    return solutions[Math.floor(Math.random() * solutions.length)];
  }

  function findBestSolution() {
    if (!solutions.length) return [];

    const valueCount = new Map<string, number>();
    for (const gameState of solutions) {
      const start = getTileKey(gameState.path[0]);
      if (start) {
        valueCount.set(start, (valueCount.get(start) ?? 0) + 1);
      }
    }

    const counts: number[] = [];
    valueCount.forEach((v) => counts.push(v));

    const maxCount = counts.length ? Math.max(...counts) : -Infinity;
    return solutions.filter(
      (s) => valueCount.get(getTileKey(s.path[0])) === maxCount,
    );
  }

  function getRandomBestSolutions() {
    const best = findBestSolution();
    return best[Math.floor(Math.random() * best.length)];
  }

  function setCoordinatesOnClick() {
    const startWord = words[yInput];
    if (!startWord || !props.gameState) return;

    const tile = props.gameState.tiles[getTileKey({ y: yInput, x: xInput })];
    if (!tile) return;

    props.setGameState(
      getGameStateFromSetup(createGameSetup({ x: xInput, y: yInput })),
    );
  }

  useEffect(() => {
    if (words.length > 0) {
      props.setGameState(
        getGameStateFromSetup(createGameSetup({ x: words[0].offset, y: 0 })),
      );
    }
  }, [words]);

  return (
    <div className="puzzle-builder">
      <h3>Word List</h3>

      <div className="form-container">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Word"
          spellCheck
        />
        <Button variant="contained" onClick={addWord}>
          Add Word
        </Button>
      </div>

      {words.map((word, index) => (
        <div className="word-list-item" key={index}>
          <div className="word-inner">{word.text}</div>
          <input
            type="number"
            value={word.offset}
            onChange={(e) => {
              const newWords = [...words];
              newWords[index].offset = parseInt(e.target.value);
              setWords(newWords);
            }}
            placeholder="offset"
          />
          <Button variant="contained" onClick={() => removeWord(index)}>
            Remove
          </Button>
        </div>
      ))}

      <div>
        <h3>
          Start Coordinates: ({props.gameState ? props.gameState.path[0].x : 0},{' '}
          {props.gameState ? props.gameState.path[0].y : 0})
        </h3>
        <div className="form-container">
          <input
            type="number"
            value={xInput}
            onChange={(e) => setXInput(parseInt(e.target.value))}
            placeholder="X"
          />
          <input
            type="number"
            value={yInput}
            onChange={(e) => setYInput(parseInt(e.target.value))}
            placeholder="Y"
          />
          <Button onClick={setCoordinatesOnClick} variant="contained">
            Set Coordinates
          </Button>
        </div>
      </div>

      <div className="form-container">
        <input
          type="text"
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          placeholder="Theme"
        />
        <input
          type="text"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          placeholder="Date"
        />
      </div>

      <div className="form-container">
        <Button onClick={SearchGames} variant="contained">
          Search games
        </Button>
        <Button
          disabled={!solutions.length}
          variant="contained"
          onClick={() => props.setGameState(getRandomSolution())}
        >
          {`Random solution (${solutions.length})`}
        </Button>
        <Button
          disabled={!solutions.length}
          variant="contained"
          onClick={() => {
            const best = getRandomBestSolutions();
            if (best) props.setGameState(best);
          }}
        >
          Random best
        </Button>
      </div>

      <div className="form-container">
        <Button
          onClick={() => validateAndUploadGame(overrideSpelling)}
          variant="contained"
          disabled={postGameMutation.isPending}
        >
          {postGameMutation.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Upload to MongoDB'
          )}
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              checked={overrideSpelling}
              onChange={(e) => setOverrideSpelling(e.target.checked)}
            />
          }
          label="Override spelling"
        />
      </div>

      {postGameMutation.isSuccess && (
        <Alert severity="success">Successfully uploaded to MongoDB!</Alert>
      )}
      {postGameMutation.isError && (
        <Alert severity="error">
          {`Failed to upload: ${(postGameMutation.error as any)?.message ?? 'Unknown error'}`}
        </Alert>
      )}
    </div>
  );
}

function validateRequiredFields(
  gameWithDate: Omit<GameWithDate, '_id'>,
): ValidationResult {
  const dateObject = new Date(gameWithDate.date);

  if (isNaN(dateObject.getTime())) {
    return { success: false, validationFailureReason: 'Invalid date' };
  }

  const game = gameWithDate.game;

  if (!game.pathString || game.pathString.length < 1) {
    return { success: false, validationFailureReason: 'Invalid path' };
  }

  if (!game.theme || game.theme.length < 1) {
    return { success: false, validationFailureReason: 'Invalid theme' };
  }

  if (!game.words || game.words.length < 1) {
    return { success: false, validationFailureReason: 'Invalid word list' };
  }

  if (!game.startCoordinates) {
    return { success: false, validationFailureReason: 'Invalid coordinates' };
  }

  return { success: true };
}

function isGameValid(
  gameWithDate: Omit<GameWithDate, '_id'>,
  overrideSpellCheck?: boolean,
): ValidationResult {
  const requiredFieldsIsValidResult = validateRequiredFields(gameWithDate);
  if (!requiredFieldsIsValidResult.success) {
    return requiredFieldsIsValidResult;
  }

  if (!overrideSpellCheck) {
    const spellingIsValidResult = validateSpelling(gameWithDate.game.words);
    if (!spellingIsValidResult.success) {
      return spellingIsValidResult;
    }
  }

  const isPossible = getSolvedPuzzle(gameWithDate.game);
  if (!isPossible.success) {
    return {
      success: false,
      validationFailureReason: 'Game is not possible',
    };
  }

  return { success: true };
}

function validateSpelling(words: Word[]): ValidationResult {
  const dictionary = new Typo('en_GB', null, null, {
    dictionaryPath: 'typo/dictionaries',
  });

  for (const w of words) {
    const spelledCorrectly = dictionary.check(w.text);
    if (!spelledCorrectly) {
      return {
        success: false,
        validationFailureReason: `misspelled: ${w.text}`,
      };
    }
  }
  return { success: true };
}

interface Props {
  gameState?: GameState;
  setGameState: (newGameState: GameState) => void;
}
